// ============================================================================
// Upload Step Handlers -> Individual handlers for each upload step.
// ============================================================================

import axios from 'axios';
import { Buffer } from 'buffer';

import { db } from './indexedDB';
import Services from 'services/Services';
import store from 'modules/state-mgmt/store';
import { getCreateWorkerParams } from 'modules/views/pages/Registration/utils';
import { getCreatePasswordParams } from 'modules/views/pages/CreatePassword/utils';
import { createGuid, extractCustomFieldCodes } from 'utils';
import { getClientIpAddress } from 'utils/ipAddress';
import { getImageFormat, getImageMimeType, uploadImagesWithPresignedUrls } from 'utils/files';
import { getTempTradeName, getUpdateWorkerBodyFromOnboarding } from 'utils/onboarding';
import { PERSONAL_INFO_FIELD_CONFIG } from 'hooks/usePersonalInformationFields';
import { CONTACT_INFO_FIELD_CONFIG } from 'hooks/useContactInformationFields';
import { TRADES_FIELD_CONFIG } from 'modules/views/pages/Trades/hooks/useTradesFields';

import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { WorkerInvitationStatus } from 'models/general';
import { StepKey } from 'modules/views/shared/Wizard/config';

export type StepHandler = (worker: IOnboardingState, languageCode: string) => Promise<IOnboardingState | null>;

// Utility functions

// ============================================================================
// Certification Helpers
// ============================================================================

// Persist updated certification items to IndexedDB.
const persistCertificationItems = async (worker: IOnboardingState, items: any[]): Promise<IOnboardingState> => {
  const updated = {
    ...worker,
    allCertifications: { items } as any,
  };
  await db.workers.update(worker.db?.id, updated);
  return updated;
};

/**
 * Delete certifications marked as isDeleted, one at a time.
 * Persists to IndexedDB after each successful deletion so progress is not lost on failure.
 */
const deleteMarkedCertifications = async (
  worker: IOnboardingState,
  items: any[],
  languageCode: string,
): Promise<{ worker: IOnboardingState; items: any[] }> => {
  const certsToDelete = items.filter(cert => cert.isDeleted);

  if (certsToDelete.length === 0) return { worker, items };

  let currentItems = items;
  let currentWorker = worker;

  for (const cert of certsToDelete) {
    // Only call API if cert exists on server (has real id, no tempId)
    if (!cert.tempId && cert.id) {
      await Services.deleteCertification(currentWorker.id, cert.id, languageCode);
    }

    // Remove this cert from items and persist immediately
    currentItems = currentItems.filter(c => c !== cert);
    currentWorker = await persistCertificationItems(currentWorker, currentItems);
  }

  return { worker: currentWorker, items: currentItems };
};

// Build the API payload for adding/updating a certification.
const buildCertificationPayload = (cert: any, workerId: string) => ({
  certificationId: cert.certification?.id || '',
  idNumber: cert.idNumber || '',
  description: cert.description || '',
  completionDate: cert.completionDate || '',
  expirationDate: cert.expirationDate || null,
  projectId: cert.project?.id || null,
  workerId,
});

/**
 * Upload new images for a certification using presigned URLs.
 * Filters to only base64 data (not yet uploaded to server).
 */
const uploadCertificationImages = async (
  workerId: string,
  certificationId: string,
  files: any[],
  languageCode: string,
): Promise<void> => {
  const filesToUpload = files.filter(
    (file: any) => !file.isDeleted && (file.url?.includes('base64') || file.image?.includes('base64')),
  );

  if (filesToUpload.length === 0) return;

  const imagesToUpload = filesToUpload.map((file: any) => {
    const imageData = file.image || file.url;
    const imageFormat = getImageFormat(imageData);
    return {
      tempId: file.id || file.tempId,
      image: imageData,
      fileName: file.fileName || `${createGuid()}.${imageFormat}`,
    };
  });

  await uploadImagesWithPresignedUrls({
    apiFunction: Services.uploadCertificate,
    apiFunctionArgs: [
      workerId,
      certificationId,
      { fileNames: imagesToUpload.map((img: any) => img.fileName) },
      languageCode,
    ],
    images: imagesToUpload,
    languageCode,
  });
};

/**
 * Add all new certifications (those with tempId), persisting state after each.
 */
const addNewCertifications = async (
  worker: IOnboardingState,
  items: any[],
  languageCode: string,
): Promise<{ worker: IOnboardingState; items: any[] }> => {
  const certsToAdd = items.filter(cert => cert.tempId && !cert.isDeleted);

  if (certsToAdd.length === 0) return { worker, items };

  let currentItems = items;
  let currentWorker = worker;

  for (const cert of certsToAdd) {
    const payload = buildCertificationPayload(cert, currentWorker.id);

    const addResponse = await Services.addCertification(currentWorker.id, payload, languageCode);
    const createdCertificationId = addResponse.data?.id;

    if (!createdCertificationId) {
      continue;
    }

    // Upload images if the cert has files with base64 data
    if (cert.files?.length > 0) {
      await uploadCertificationImages(currentWorker.id, createdCertificationId, cert.files, languageCode);
    }

    // Replace the temp cert with the server-returned ID and persist
    currentItems = currentItems.map(item =>
      item.tempId === cert.tempId
        ? { ...item, id: createdCertificationId, tempId: undefined, isOffline: undefined }
        : item,
    );
    currentWorker = await persistCertificationItems(currentWorker, currentItems);
  }

  return { worker: currentWorker, items: currentItems };
};

/**
 * Edit all modified certifications (those with isOffline: true and a real server id, no tempId).
 * For each cert:
 *  1. Update cert metadata via API
 *  2. Delete files marked isDeleted via API
 *  3. Upload new files (base64 data) via presigned URLs
 *  4. Persist to IndexedDB after each successful edit
 */
const editModifiedCertifications = async (
  worker: IOnboardingState,
  items: any[],
  languageCode: string,
): Promise<{ worker: IOnboardingState; items: any[] }> => {
  const certsToEdit = items.filter(cert => cert.isOffline && !cert.tempId && !cert.isDeleted && cert.id);

  if (certsToEdit.length === 0) return { worker, items };

  let currentItems = items;
  let currentWorker = worker;

  for (const cert of certsToEdit) {
    const payload = buildCertificationPayload(cert, currentWorker.id);

    // Step 1: Update certification metadata
    await Services.updateCertification(currentWorker.id, cert.id, payload, languageCode);

    // Step 2: Delete files marked as isDeleted
    const filesToDelete = (cert.files || []).filter((f: any) => f.isDeleted && f.id);
    if (filesToDelete.length > 0) {
      await Services.deleteCertificationFiles(
        currentWorker.id,
        cert.id,
        {
          workerId: currentWorker.id,
          workerCertificationId: cert.id,
          fileIds: filesToDelete.map((f: any) => f.id),
        },
        languageCode,
      );
    }

    // Step 3: Upload new images (base64 data that hasn't been uploaded yet)
    const newFiles = (cert.files || []).filter(
      (f: any) => !f.isDeleted && (f.url?.includes('base64') || f.image?.includes('base64')),
    );
    if (newFiles.length > 0) {
      await uploadCertificationImages(currentWorker.id, cert.id, newFiles, languageCode);
    }

    // Step 4: Update the cert in items — remove offline flags and deleted files, then persist
    currentItems = currentItems.map(item =>
      item.id === cert.id
        ? {
            ...item,
            isOffline: undefined,
            files: (item.files || []).filter((f: any) => !f.isDeleted),
          }
        : item,
    );
    currentWorker = await persistCertificationItems(currentWorker, currentItems);
  }

  return { worker: currentWorker, items: currentItems };
};

/**
 * Async refresh of worker data from the backend after successful upload.
 * Replaces most worker fields with fresh server data while preserving
 * local-only fields (temp, uploadingDetails, db, certifications, etc.).
 */
export const refreshWorkerData = async (dbId: string, workerId: string, languageCode: string): Promise<void> => {
  try {
    const response = await Services.getWorker(workerId, languageCode);
    const serverData = response?.data;

    const currentWorker = await db.workers.get(dbId);

    // Merge server data while preserving local-only fields
    const refreshedWorker = {
      ...(currentWorker || {}),
      ...(serverData || {}),
      // Preserve local-only fields
      temp: currentWorker?.temp,
      uploadingDetails: currentWorker?.uploadingDetails,
      db: currentWorker?.db,
      // Preserve separately-fetched data (not part of getWorker response)
      allCertifications: currentWorker?.allCertifications,
      certificationGroups: currentWorker?.certificationGroups,
      existingConsentFormForAllCompanies: currentWorker?.existingConsentFormForAllCompanies,
      assignedCompanies: currentWorker?.assignedCompanies,
    };

    await db.workers.update(dbId, refreshedWorker);
  } catch (error) {
    // Silent failure — worker is already marked COMPLETED, this is best-effort
  }
};

// ============================================================================
// Step Handlers
// ============================================================================

/**
 * Create worker in the backend.
 */
export const handleWorkerCreation: StepHandler = async (worker, languageCode) => {
  const payload = getCreateWorkerParams({ ...worker, employers: worker.temp?.employers });
  const response = await Services.postWorker(payload, languageCode);
  const createdWorker = response.data;

  const updatedWorker = {
    ...worker,
    canEditEmail: !payload?.email,
    canEditMobilePhoneNumber: !payload?.mobilePhoneNumber,
    createdAt: createdWorker?.createdAt,
    invitationStatus: createdWorker?.invitationStatus,
    inviteMethod: createdWorker?.inviteMethod,
    lastUpdatedAt: createdWorker?.lastUpdatedAt,
    id: createdWorker?.id,
    userId: createdWorker?.userId,
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        workerCreation: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Create password for the worker.
 */
export const handlePasswordCreation: StepHandler = async (worker, languageCode) => {
  const payload = getCreatePasswordParams({
    ...worker,
    inviteMethod: 0,
    password: worker.temp?.password,
  });

  await Services.register(payload, languageCode);

  const updatedWorker = {
    ...worker,
    invitationStatus: WorkerInvitationStatus.ACCEPTED,
    temp: {
      ...worker.temp,
      firstName: null,
      lastName: null,
      email: null,
      mobilePhoneNumber: null,
      dateOfBirth: null,
      password: null,
    },
    uploadingDetails: {
      ...worker.uploadingDetails,
      sectionsCompleted: {
        ...worker.uploadingDetails?.sectionsCompleted,
        [StepKey.REGISTRATION]: true,
      },
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        workerPasswordCreation: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Upload profile picture (3-step process).
 */
export const handleProfilePicture: StepHandler = async (worker, languageCode) => {
  const image = worker.pictureUrl;

  if (!image || !image.includes('base64')) {
    // If no image, mark as complete (skip)
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          profilePicture: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  // Step 1: Get presigned URL
  const fileExtension = getImageFormat(image);
  const fileName = `${createGuid()}.${fileExtension}`;

  const response = await Services.uploadImage(
    worker.userId,
    {
      fileName,
    },
    languageCode,
  );

  // Step 2: Upload to S3
  const contentType = getImageMimeType(fileExtension);
  const config = {
    method: 'put',
    url: response.data.url,
    headers: {
      'Content-Type': contentType,
    },
    data: Buffer.from(image.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
  };

  await axios(config);

  // Step 3: Confirm upload
  await Services.confirmImageUpload(response.data.fileId, languageCode);

  const updatedWorker = {
    ...worker,
    uploadingDetails: {
      ...worker.uploadingDetails,
      sectionsCompleted: {
        ...worker.uploadingDetails?.sectionsCompleted,
        [StepKey.TAKE_PHOTO]: true,
      },
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        profilePicture: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Upload government documents.
 * Also deletes files marked with isDeleted: true.
 */
export const handleGovDocuments: StepHandler = async (worker, languageCode) => {
  // Step 1: Delete files marked as isDeleted
  const filesToDelete = worker.governmentDocumentFiles?.filter(file => file.isDeleted && file.id) || [];

  if (filesToDelete.length > 0) {
    const deletePromises = filesToDelete.map(file =>
      Services.deleteGovernmentDocument(worker.id, file.id, languageCode),
    );
    await Promise.all(deletePromises);

    const updatedWorker = {
      ...worker,
      governmentDocumentFiles: worker.governmentDocumentFiles?.filter(file => !file.isDeleted),
    };

    await db.workers.update(worker.db?.id, updatedWorker);
  }

  // Step 2: Filter only files with tempId (new files not yet uploaded)
  const filesToUpload =
    worker.governmentDocumentFiles?.filter(file => file.tempId && file.image?.includes('base64') && !file.isDeleted) ||
    [];

  if (filesToUpload.length > 0) {
    // Get presigned URLs for all files
    const response = await Services.uploadGovernmentDocuments(
      worker.id,
      { fileNames: filesToUpload.map(file => file.fileName) },
      languageCode,
    );

    const fileInfos = response.data;

    // Upload each file to S3 in parallel
    const uploadPromises = fileInfos.map((info, index) => {
      const file = filesToUpload[index];
      const imageFormat = getImageFormat(file.image);
      const contentType = getImageMimeType(imageFormat);

      return axios({
        method: 'put',
        url: info.url,
        headers: { 'Content-Type': contentType },
        data: Buffer.from(file.image.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
      }).then(() => {
        // Confirm upload
        return Services.confirmImageUpload(info.fileId, languageCode);
      });
    });

    await Promise.all(uploadPromises);
  }

  // Fetch fresh worker data to get updated governmentDocumentFiles with CDN URLs
  const workerResponse = await Services.getWorker(worker.id, languageCode);

  // Update worker state with fresh data from API
  const updatedWorker = {
    ...worker,
    governmentDocumentFiles: workerResponse?.data?.governmentDocumentFiles || [],
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        govDocuments: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Combined handler for personalInformation, contactInformation, and trades.
 * All three use the same updateWorker API, so we send one combined request
 * and mark all pending flags as complete.
 */
export const handleWorkerUpdate: StepHandler = async (worker, languageCode) => {
  const pendingUploads = worker.uploadingDetails?.pendingUploads;
  const isPersonalInfoPending = pendingUploads?.personalInformation;
  const isContactInfoPending = pendingUploads?.contactInformation;
  const isTradesPending = pendingUploads?.trades;

  // If none are pending, skip
  if (!worker.id || (!isPersonalInfoPending && !isContactInfoPending && !isTradesPending)) {
    return worker;
  }

  // Build the combined payload from the worker object (same as getUpdateWorkerBodyFromOnboarding)
  const state = store.getState();
  const skilledTrades = state?.cache?.skilledTrades?.[languageCode] || [];
  const requestBody = getUpdateWorkerBodyFromOnboarding(worker, { skilledTrades });

  // Only include workerCustomFieldCodesToValidate for steps that are actually pending
  const workerCustomFieldCodesToValidate = [
    ...(isPersonalInfoPending ? extractCustomFieldCodes(PERSONAL_INFO_FIELD_CONFIG) : []),
    ...(isContactInfoPending ? extractCustomFieldCodes(CONTACT_INFO_FIELD_CONFIG) : []),
    ...(isTradesPending ? extractCustomFieldCodes(TRADES_FIELD_CONFIG) : []),
  ];
  requestBody.workerCustomFieldCodesToValidate = workerCustomFieldCodesToValidate;

  // Call the updateWorker API
  await Services.updateWorker(worker.id, requestBody, languageCode);

  // Mark all three steps as complete
  const updatedWorker = {
    ...worker,
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        personalInformation: false,
        contactInformation: false,
        trades: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Handler for the employers step.
 * Assigns the worker to companies within the project.
 * Fetches already-assigned companies via API (not cache) to avoid stale data.
 */
export const handleEmployers: StepHandler = async (worker, languageCode) => {
  const projectId = worker.temp?.project?.value || worker.db?.projectId;

  // Determine companies to assign (same logic as handleWorkerCompanyAssignments)
  const companies =
    worker.temp?.employers?.map((employer: any) => ({
      id: employer.value,
      name: employer.label,
    })) || [];

  // Fetch already-assigned companies via API (not from cache)
  const assignedCompaniesResponse = await Services.getWorkerProjectCompanies(worker.id, projectId, languageCode);
  const assignedCompanies = assignedCompaniesResponse?.data || [];

  // Filter out already-assigned companies
  const notAssignedCompanies = companies.filter(
    (company: any) => !assignedCompanies.some((assigned: any) => assigned.id === company.id),
  );

  if (notAssignedCompanies.length === 0) {
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          employers: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  // Build invite method data
  let inviteMethodValue = {};
  if (worker.inviteMethod === 1) inviteMethodValue = { email: worker.email || '' };

  if (worker.inviteMethod === 2) inviteMethodValue = { mobilePhoneNumber: worker.mobilePhoneNumber || '' };

  if (worker.inviteMethod === 3)
    inviteMethodValue = { email: worker.email || '', mobilePhoneNumber: worker.mobilePhoneNumber || '' };

  // Prepare assignment request data
  const assignRequestData = notAssignedCompanies.map((company: any) => ({
    id: worker.id,
    companyId: company.id,
    inviteMethod: worker.inviteMethod,
    ...inviteMethodValue,
  }));

  // Call the assign API
  await Services.assignWorkerToProject(projectId, assignRequestData, languageCode);

  // Mark step as complete
  const updatedWorker = {
    ...worker,
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        employers: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

// ============================================================================
// Certification Step Handler
// ============================================================================

/**
 * Handle certifications upload.
 * - Deletes certifications marked with isDeleted: true (only calls API if cert exists on the server).
 * - Adds new certifications that have a tempId (created offline), including image uploads.
 * - Edits modified certifications (isOffline: true, real server id), including file deletions and uploads.
 * - Re-fetches all certifications from the server after processing.
 *
 * Each individual operation is persisted to IndexedDB immediately after success,
 * so partial progress is preserved if a later operation fails.
 */
export const handleCertifications: StepHandler = async (worker, languageCode) => {
  let currentItems: any[] = (worker.allCertifications as any)?.items || [];

  // Step 1: Delete certifications marked as isDeleted
  const deleteResult = await deleteMarkedCertifications(worker, currentItems, languageCode);
  worker = deleteResult.worker;
  currentItems = deleteResult.items;

  // Step 2: Add new certifications (those with tempId)
  const addResult = await addNewCertifications(worker, currentItems, languageCode);
  worker = addResult.worker;
  currentItems = addResult.items;

  // Step 3: Edit modified certifications (isOffline with real server id)
  const editResult = await editModifiedCertifications(worker, currentItems, languageCode);
  worker = editResult.worker;

  // Step 4: Re-fetch all certifications from server
  const certificationsResponse = await Services.getAllCertifications(worker.id, languageCode);

  const updatedWorker = {
    ...worker,
    allCertifications: certificationsResponse.data || [],
    uploadingDetails: {
      ...worker.uploadingDetails,
      sectionsCompleted: {
        ...worker.uploadingDetails?.sectionsCompleted,
        [StepKey.CERTIFICATIONS]: true,
      },
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        certifications: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

// ============================================================================
// Consent Form Step Handlers
// ============================================================================

/**
 * Upload consent form signature (3-step presigned URL process).
 * Same pattern as handleProfilePicture / uploadSignatureToS3 thunk.
 */
export const handleSignature: StepHandler = async (worker, languageCode) => {
  const signData = worker.temp?.signature;

  if (!signData || !signData.includes('base64')) {
    // No signature data — skip this step
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          signature: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  // Step 1: Get presigned URL
  const imageFormat = getImageFormat(signData);
  const fileName = `${createGuid()}.${imageFormat}`;

  const response = await Services.uploadSignature(worker.id, { fileName }, languageCode);

  // Step 2: Upload to S3
  const contentType = getImageMimeType(imageFormat);
  const config = {
    method: 'put' as const,
    url: response.data.url,
    headers: {
      'Content-Type': contentType,
    },
    data: Buffer.from(signData.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
  };

  await axios(config);

  // Step 3: Confirm upload
  await Services.confirmImageUpload(response.data.fileId, languageCode);

  const updatedWorker = {
    ...worker,
    uploadingDetails: {
      ...worker.uploadingDetails,
      sectionsCompleted: {
        ...worker.uploadingDetails?.sectionsCompleted,
        [StepKey.REGISTRATION]: true,
      },
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        signature: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

export const handleCustomConsentAcceptances: StepHandler = async (worker, languageCode) => {
  const queued = worker.temp?.customConsentAcceptances;
  const projectId = worker.db?.projectId || worker.temp?.project?.value;

  const clearPendingFlag = async () => {
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          customConsentAcceptances: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  };

  if (!queued?.consentAgreementContentIds?.length || !queued?.consentAgreementIds?.length) {
    // Nothing queued — skip gracefully without an API call.
    return clearPendingFlag();
  }

  // Subtract anything already confirmed, so a retry after a partial success cannot double-submit.
  const alreadyAccepted = new Set<string>(worker.acceptedCustomConsentIds || []);
  const contentIdsToSend = queued.consentAgreementContentIds.filter((id: string) => !alreadyAccepted.has(id));
  if (!contentIdsToSend.length) {
    return clearPendingFlag();
  }

  const ipAddress = await getClientIpAddress();

  await Services.acceptConsentAgreements(
    worker.id,
    {
      projectId,
      source: 'WorkerOnboarding',
      ipAddress,
      consentAgreementContentIds: contentIdsToSend,
      consentAgreementIds: queued.consentAgreementIds,
    },
    languageCode,
  );

  // Single atomic write: record the acceptance, drain the queue and clear the flag together.
  const updatedWorker = {
    ...worker,
    acceptedCustomConsentIds: Array.from(alreadyAccepted).concat(contentIdsToSend),
    temp: {
      ...worker.temp,
      customConsentAcceptances: null,
    },
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        customConsentAcceptances: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

type ConsentFormPayload = { companyId: string; formPayload: any[] };

/**
 * A trade typed offline was picked on the consent form before it had an id, so the payload
 * holds a placeholder. The trades step has uploaded it by now — swap in the id the backend gave it,
 * otherwise the backend saves the placeholder text as a brand-new trade.
 */
const resolveTempTradeIds = async (
  worker: IOnboardingState,
  payloads: ConsentFormPayload[],
  languageCode: string,
): Promise<ConsentFormPayload[]> => {
  const hasPlaceholder = payloads.some(payload => payload.formPayload?.some(field => getTempTradeName(field.value)));
  if (!hasPlaceholder) return payloads;

  const { data } = await Services.getWorker(worker.id, languageCode);
  const normalize = (name: string) => name?.trim().toLowerCase();
  const tradeIdByName = new Map<string, string>(
    (data?.trades || []).map((trade: any) => [normalize(trade.name), trade.id]),
  );

  return payloads.map(payload => ({
    ...payload,
    formPayload: payload.formPayload.map(field => {
      const tradeName = getTempTradeName(field.value);
      if (tradeName === null) return field;

      const tradeId = tradeIdByName.get(normalize(tradeName));
      if (!tradeId) throw new Error(`Trade "${tradeName}" was not found on the worker after upload`);
      return { ...field, value: tradeId };
    }),
  }));
};

/**
 * Submit consent form data (accept project for each company).
 * Calls Services.acceptProject per company with its consent form field values.
 */
export const handleConsentFormData: StepHandler = async (worker, languageCode) => {
  const consentFormPayloads = worker.temp?.consentFormPayloads;
  const projectId = worker.db?.projectId || worker.temp?.project?.value;

  if (!consentFormPayloads || consentFormPayloads.length === 0) {
    // No consent form payloads — skip this step
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        sectionsCompleted: {
          ...worker.uploadingDetails?.sectionsCompleted,
          [StepKey.CONSENT_FORM]: true,
        },
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          consentFormData: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  const resolvedPayloads = await resolveTempTradeIds(worker, consentFormPayloads, languageCode);

  // Submit consent form data for each company in parallel
  const acceptPromises = resolvedPayloads.map((payload: ConsentFormPayload) =>
    Services.acceptProject(
      worker.userId!,
      projectId,
      payload.companyId,
      { consentFormFields: payload.formPayload },
      languageCode,
    ),
  );

  await Promise.all(acceptPromises);

  // Fetch latest assigned companies from backend after accepting
  const assignedCompaniesResponse = await Services.getWorkerProjectCompanies(worker.id, projectId, languageCode);
  const latestAssignedCompanies = assignedCompaniesResponse?.data || worker.assignedCompanies || [];

  // Mark consent form as complete and clear temp consent data
  const updatedWorker = {
    ...worker,
    assignedCompanies: latestAssignedCompanies,
    temp: {
      ...worker.temp,
      signature: null,
      consentFormPayloads: null, //* Remove this line if you want to keep the consent form data in temp after upload
    },
    uploadingDetails: {
      ...worker.uploadingDetails,
      sectionsCompleted: {
        ...worker.uploadingDetails?.sectionsCompleted,
        [StepKey.CONSENT_FORM]: true,
      },
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        consentFormData: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};

/**
 * Submit consent agreement acceptances collected during the registration consent flow.
 * Reads accumulated agreement IDs from worker.temp.consentAcceptances.
 */
export const handleConsentAcceptances: StepHandler = async (worker, languageCode) => {
  const consentAgreementContentIds = worker.temp?.consentAcceptances;
  const projectId = worker.db?.projectId;

  if (!consentAgreementContentIds || consentAgreementContentIds.length === 0) {
    const updatedWorker = {
      ...worker,
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          consentAcceptances: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  // Subtract anything already confirmed so a retry after a partial success is a no-op.
  const alreadyAccepted = new Set<string>(worker.acceptedStandardConsentIds || []);
  const contentIdsToSend = consentAgreementContentIds.filter((id: string) => !alreadyAccepted.has(id));

  if (!contentIdsToSend.length) {
    const updatedWorker = {
      ...worker,
      temp: { ...worker.temp, consentAcceptances: [] },
      uploadingDetails: {
        ...worker.uploadingDetails,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          consentAcceptances: false,
        },
      },
    };
    await db.workers.update(worker.db?.id, updatedWorker);
    return updatedWorker;
  }

  const ipAddress = await getClientIpAddress();

  await Services.acceptConsentAgreements(
    worker.id,
    {
      projectId,
      source: 'WorkerOnboarding',
      ipAddress,
      consentAgreementContentIds: contentIdsToSend,
    },
    languageCode,
  );

  const updatedWorker = {
    ...worker,
    acceptedStandardConsentIds: Array.from(alreadyAccepted).concat(contentIdsToSend),
    temp: {
      ...worker.temp,
      consentAcceptances: [],
    },
    uploadingDetails: {
      ...worker.uploadingDetails,
      pendingUploads: {
        ...worker.uploadingDetails?.pendingUploads,
        consentAcceptances: false,
      },
    },
  };

  await db.workers.update(worker.db?.id, updatedWorker);

  return updatedWorker;
};
